"use client";

import { useEffect, useRef, useState } from "react";
import { Upload, Trash2, Loader2 } from "lucide-react";
import { ResponsiveModal, useUniversalAuth } from "@west-tennessee-consulting/ui";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { UserAvatar } from "@/components/ui/user-avatar";
import { useProfileAvatar } from "@/lib/hooks/user/use-profile-avatar";

const ACCEPTED = ["image/png", "image/jpeg", "image/webp", "image/gif"];
const MAX_BYTES = 5 * 1024 * 1024;

interface ProfilePictureModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Manage the current user's profile picture: preview, upload, or remove.
 * Controlled + self-contained (reads its own auth + avatar), so it can be
 * dropped into the header dropdown AND the /account Profile tab unchanged.
 */
export function ProfilePictureModal({ open, onOpenChange }: ProfilePictureModalProps) {
  const { appUser } = useUniversalAuth();
  const { avatarUrl, upload, remove, isMutating } = useProfileAvatar();

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Revoke object URLs to avoid leaks; reset transient state when closed.
  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  const reset = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
    setFile(null);
    setError(null);
  };

  const close = () => {
    reset();
    onOpenChange(false);
  };

  const onPick = (e: React.ChangeEvent<HTMLInputElement>) => {
    const picked = e.target.files?.[0];
    e.target.value = ""; // allow re-picking the same file
    if (!picked) return;
    if (!ACCEPTED.includes(picked.type)) {
      setError("Image must be a PNG, JPEG, WebP, or GIF.");
      return;
    }
    if (picked.size > MAX_BYTES) {
      setError("Image must be smaller than 5MB.");
      return;
    }
    setError(null);
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setFile(picked);
    setPreviewUrl(URL.createObjectURL(picked));
  };

  const onSave = async () => {
    if (!file) return;
    setError(null);
    try {
      await upload(file);
      close();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to upload image.");
    }
  };

  const onRemove = async () => {
    setError(null);
    try {
      await remove();
      close();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to remove image.");
    }
  };

  const displayUrl = previewUrl ?? avatarUrl;

  return (
    <ResponsiveModal
      open={open}
      onOpenChange={(next) => (next ? onOpenChange(true) : close())}
      title="Profile picture"
      description="Upload a photo, or use your initials. Visible to your team across Hub."
    >
      <div className="flex flex-col items-center gap-4 py-2">
        <UserAvatar
          avatarUrl={displayUrl}
          name={appUser ? `${appUser.first_name ?? ""} ${appUser.last_name ?? ""}`.trim() : null}
          email={appUser?.email}
          className="size-24 text-2xl"
        />

        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <input
          ref={fileInputRef}
          type="file"
          accept={ACCEPTED.join(",")}
          className="hidden"
          onChange={onPick}
        />

        <div className="flex w-full flex-col gap-2 sm:flex-row sm:justify-center">
          <Button
            type="button"
            variant="outline"
            onClick={() => fileInputRef.current?.click()}
            disabled={isMutating}
          >
            <Upload className="mr-2 h-4 w-4" />
            Choose image
          </Button>
          {avatarUrl && !file && (
            <Button type="button" variant="ghost" onClick={onRemove} disabled={isMutating}>
              {isMutating ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Trash2 className="mr-2 h-4 w-4 text-destructive" />
              )}
              Remove photo
            </Button>
          )}
        </div>
      </div>

      <div className="mt-2 flex justify-end gap-2 border-t pt-4">
        <Button type="button" variant="outline" onClick={close} disabled={isMutating}>
          Cancel
        </Button>
        <Button type="button" onClick={onSave} disabled={!file || isMutating}>
          {isMutating ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
          Save photo
        </Button>
      </div>
    </ResponsiveModal>
  );
}
